import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Auth0Client, User } from '@auth0/auth0-spa-js';
import { useNavigate } from 'react-router';
import { AuthContext } from './authContext';
import { hasAuthErrorParams, hasAuthSuccessParams } from './search-params';
import { initialAuthState } from './authState';

export type RedirectAppState = {
  returnTo?: string;
};

export function AuthProvider({
  client,
  onRedirectCallback,
  children,
}: {
  client: Auth0Client;
  onRedirectCallback: (appState?: RedirectAppState) => void;
  children: ReactNode;
}) {
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const initialized = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    (async (): Promise<void> => {
      try {
        let user: User | undefined;
        if (hasAuthSuccessParams()) {
          const { appState } = await client.handleRedirectCallback<RedirectAppState>();
          user = await client.getUser();
          onRedirectCallback(appState);
        } else if (hasAuthErrorParams()) {
          setIsAuthenticated(false);
          await navigate('/unauthorized');
          return;
        } else {
          await client.checkSession();
          user = await client.getUser();
        }
        setIsAuthenticated(!!user);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(e);
        }
      }
    })();
  }, [client, navigate, onRedirectCallback]);

  const login = useCallback(async () => {
    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/home`,
      },
    });
  }, [client]);

  const logout = useCallback(async () => {
    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [client]);

  return (
    <AuthContext.Provider value={{ ...initialAuthState, error, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
